"use client";

import { clsx } from "clsx";
import type { MouseEvent, ReactNode } from "react";

import { PreviewPlayerButton } from "@ui/components/core/PreviewPlayer/PreviewPlayerButton";
import { PreviewPlayerProgressRing } from "@ui/components/core/PreviewPlayer/PreviewPlayerProgressRing";
import type { usePreviewPlayer } from "@ui/components/core/PreviewPlayer/usePreviewPlayer";

interface VoicePreviewAvatarProps {
  /** Avatar shown at rest; faded out on hover/focus/play to reveal the preview indicator. */
  avatar: ReactNode;
  /** Whether this voice has a playable preview (gates the preview indicator). */
  hasPreview: boolean;
  isPlaying: boolean;
  isLoading: boolean;
  progressRingProps: ReturnType<typeof usePreviewPlayer>["progressRingProps"];
  /** Active row — keeps the indicator shown and the avatar faded even when not hovered. */
  isSelected: boolean;
  /**
   * `true` (default): the indicator is a focusable button that toggles the preview itself.
   * `false`: a non-interactive `aria-hidden` indicator — the parent row owns the click target.
   */
  interactive?: boolean;
  /** aria-label/title for the interactive preview button. */
  previewActionLabel?: string;
  /** Click handler for the interactive preview button (stopPropagation is handled internally). */
  onActivate?: () => void;
}

/**
 * The avatar-with-preview-overlay used in every voice row (TTS full-page list, AI Speech Classifier
 * similar-voices list, bento-grid voice list): an avatar that fades out on hover/play to reveal a
 * play/pause indicator. Must live inside a `group/media-row` ancestor (e.g. {@link MediaRow}), whose
 * hover/focus state drives the swap.
 */
export function VoicePreviewAvatar({
  avatar,
  hasPreview,
  isPlaying,
  isLoading,
  progressRingProps,
  isSelected,
  interactive = true,
  previewActionLabel,
  onActivate,
}: VoicePreviewAvatarProps) {
  const indicator = (
    <PreviewPlayerButton inverted isActive={isPlaying} isLoading={isLoading} size='xs'>
      <PreviewPlayerProgressRing {...progressRingProps} inverted size='xs' />
    </PreviewPlayerButton>
  );

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onActivate?.();
  };

  return (
    <div className='tw-relative tw-size-7 tw-shrink-0'>
      <div
        className={clsx(
          "tw-transition-opacity tw-duration-150",
          hasPreview && "md:group-hover/media-row:tw-opacity-0 group-focus-within/media-row:tw-opacity-0",
          (isPlaying || (isSelected && hasPreview)) && "tw-opacity-0",
        )}
      >
        {avatar}
      </div>
      {hasPreview &&
        (interactive ? (
          <button
            aria-label={previewActionLabel}
            className={clsx(
              "tw-group/preview-button tw-absolute tw-inset-0 tw-flex tw-cursor-pointer tw-appearance-none tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-transparent tw-p-0 tw-outline-none tw-transition-opacity tw-duration-150 focus-visible:tw-outline-focus",
              "tw-pointer-events-none md:group-hover/media-row:tw-pointer-events-auto md:group-hover/media-row:tw-opacity-100 group-focus-within/media-row:tw-pointer-events-auto group-focus-within/media-row:tw-opacity-100",
              !isPlaying && !isSelected && "tw-opacity-0",
              (isPlaying || isSelected) && "tw-pointer-events-auto tw-opacity-100",
            )}
            onClick={handleClick}
            title={previewActionLabel}
            type='button'
          >
            {indicator}
          </button>
        ) : (
          <div
            aria-hidden='true'
            className={clsx(
              "tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-transition-opacity tw-duration-150",
              "md:group-hover/media-row:tw-opacity-100 group-focus-within/media-row:tw-opacity-100",
              !isPlaying && !isSelected && "tw-opacity-0",
            )}
          >
            {indicator}
          </div>
        ))}
    </div>
  );
}
